/**
 * Operations › Analytics (the usage analytics contract, Y:\scratch\keep\gyro-usage-analytics-20261029\CONTRACT-usage-analytics.md
 * sections 2 and 5): the wire shapes of every admin read under /v1/admin/usage, written from the contract and never imported
 * from the service; a normalizer for each one (a missing or odd field reads as empty or null, never as NaN or a crash); the
 * lens (window, surface, audience, hidden admin screens) and its three presets, remembered per device; ranking, deltas,
 * labels, Monday-week and day helpers; and the CSV writer with its formula-injection guard.
 *
 * Every number the page shows comes from one of these reads: nothing here sums raw rows. The sections (screens/usage/*)
 * are thin; this module holds the arithmetic so test/usage.test.ts can prove it without a DOM.
 */

// ------------------------------------------------------------------------------------------------ the lens (contract section 2)

export type UsageWindow = 7 | 30 | 90 | 180;
export const USAGE_WINDOWS: readonly UsageWindow[] = [7, 30, 90, 180];
export const DEFAULT_WINDOW: UsageWindow = 30;

export type UsageSurface = 'all' | 'web' | 'desktop' | 'office' | 'admin';
export const USAGE_SURFACES: readonly UsageSurface[] = ['all', 'web', 'desktop', 'office', 'admin'];
export const SURFACE_LABELS: Record<UsageSurface, string> = { all: 'All surfaces', web: 'Web widget', desktop: 'Desktop', office: 'Office pane', admin: 'Gyro Admin' };

export type UsageAudience = 'general' | 'internal' | 'all';
export const USAGE_AUDIENCES: readonly UsageAudience[] = ['general', 'internal', 'all'];

/** What every read is asked with. `window` is left out of the realtime read (the contract: no window). */
export interface UsageLens {
  window: UsageWindow;
  surface: UsageSurface;
  audience: UsageAudience;
  /** True: the admin area's own screens and actions are left out of rankings, journeys, realtime screens and top screens. */
  hideAdmin: boolean;
}

/**
 * The three presets (the brief's "Audience lens"): General users is the default and leaves the marked people and the admin
 * screens out; Internal team shows only the marked people with the admin screens; Everyone has no person filter.
 */
export type AudiencePreset = 'general-users' | 'internal-team' | 'everyone';
export const AUDIENCE_PRESETS: readonly AudiencePreset[] = ['general-users', 'internal-team', 'everyone'];
export const PRESET_LABELS: Record<AudiencePreset, string> = { 'general-users': 'General users', 'internal-team': 'Internal team', everyone: 'Everyone' };
const PRESET_VALUES: Record<AudiencePreset, { audience: UsageAudience; hideAdmin: boolean }> = {
  'general-users': { audience: 'general', hideAdmin: true },
  'internal-team': { audience: 'internal', hideAdmin: false },
  everyone: { audience: 'all', hideAdmin: false },
};
const PRESET_BY_AUDIENCE: Record<UsageAudience, AudiencePreset> = { general: 'general-users', internal: 'internal-team', all: 'everyone' };

export const DEFAULT_LENS: UsageLens = { window: DEFAULT_WINDOW, surface: 'all', ...PRESET_VALUES['general-users'] };

/** The preset a lens is in: it follows the audience alone, so the hidden chip can change without renaming the preset. */
export function presetOf(lens: Pick<UsageLens, 'audience'>): AudiencePreset {
  return PRESET_BY_AUDIENCE[lens.audience];
}

/** The lens with a preset applied: both halves change (the chip follows the preset when a preset is picked). */
export function withPreset(lens: UsageLens, preset: AudiencePreset): UsageLens {
  return { ...lens, ...PRESET_VALUES[preset] };
}

const isWindow = (value: unknown): value is UsageWindow => (USAGE_WINDOWS as readonly unknown[]).includes(value);
const isSurface = (value: unknown): value is UsageSurface => (USAGE_SURFACES as readonly unknown[]).includes(value);
const isAudience = (value: unknown): value is UsageAudience => (USAGE_AUDIENCES as readonly unknown[]).includes(value);

/** Where the audience half of the lens is kept in this browser (a convenience per device, not a setting anyone else sees). */
export const LENS_STORAGE_KEY = 'gyro-admin.usage.lens';

/** The stored audience and chip: the text of the storage key, read back. */
export interface StoredLens { audience: UsageAudience; hideAdmin: boolean }

/**
 * What device storage holds, read tolerantly: a missing key, junk, a wrong type or an unknown audience each fall back to the
 * default (General users, admin screens hidden) field by field, so a stale or edited value never breaks the page.
 */
export function parseStoredLens(raw: string | null | undefined): StoredLens {
  const fallback: StoredLens = { audience: DEFAULT_LENS.audience, hideAdmin: DEFAULT_LENS.hideAdmin };
  if (typeof raw !== 'string' || !raw.trim()) return fallback;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return fallback;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return fallback;
  const fields = parsed as Record<string, unknown>;
  return {
    audience: isAudience(fields.audience) ? fields.audience : fallback.audience,
    hideAdmin: typeof fields.hideAdmin === 'boolean' ? fields.hideAdmin : fallback.hideAdmin,
  };
}

/** The text stored for a lens: its audience and chip only (the window and surface live in the address). */
export function serializeStoredLens(lens: Pick<UsageLens, 'audience' | 'hideAdmin'>): string {
  return JSON.stringify({ audience: lens.audience, hideAdmin: lens.hideAdmin });
}

/** The stored half of the lens from this browser, or the default (storage blocked or empty). */
export function readStoredLens(storage: Pick<Storage, 'getItem'> | null | undefined): StoredLens {
  try {
    return parseStoredLens(storage?.getItem(LENS_STORAGE_KEY));
  } catch {
    return parseStoredLens(null);
  }
}

export function writeStoredLens(storage: Pick<Storage, 'setItem'> | null | undefined, lens: Pick<UsageLens, 'audience' | 'hideAdmin'>): void {
  try {
    storage?.setItem(LENS_STORAGE_KEY, serializeStoredLens(lens));
  } catch {
    // A convenience only: it is simply not remembered.
  }
}

/** The window and surface a location carries (`?window=30&surface=web`); anything unreadable is the default. */
export function lensFromSearch(search: string, stored: StoredLens): UsageLens {
  const params = new URLSearchParams(search);
  const window = Number(params.get('window'));
  const surface = params.get('surface');
  return { window: isWindow(window) ? window : DEFAULT_WINDOW, surface: isSurface(surface) ? surface : 'all', audience: stored.audience, hideAdmin: stored.hideAdmin };
}

/** The query string that keeps the window and surface in the address, beside the page's other parameters. */
export function lensSearch(lens: Pick<UsageLens, 'window' | 'surface'>, others: URLSearchParams = new URLSearchParams()): string {
  const params = new URLSearchParams(others);
  if (lens.window === DEFAULT_WINDOW) params.delete('window');
  else params.set('window', String(lens.window));
  if (lens.surface === 'all') params.delete('surface');
  else params.set('surface', lens.surface);
  const text = params.toString();
  return text ? `?${text}` : '';
}

/** The lens parameters as every read takes them (contract section 2): `window`, `surface`, `audience`, `hide_admin=1|0`. */
export interface UsageQuery { window?: UsageWindow; surface: UsageSurface; audience: UsageAudience; hide_admin: '1' | '0'; [extra: string]: string | number | undefined }

export function lensQuery(lens: UsageLens, extra: Record<string, string | number | undefined> = {}): UsageQuery {
  return { window: lens.window, surface: lens.surface, audience: lens.audience, hide_admin: lens.hideAdmin ? '1' : '0', ...extra };
}

/** The realtime read's query: the lens without its window (the contract: last 5 and 30 minutes, no window). */
export function realtimeQuery(lens: UsageLens): UsageQuery {
  return { surface: lens.surface, audience: lens.audience, hide_admin: lens.hideAdmin ? '1' : '0' };
}

/** `?window=30&surface=all&audience=general&hide_admin=1`: every set value in the order given; nothing when all are empty. */
export function usageQueryString(query: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, value === true ? '1' : value === false ? '0' : String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

/** A sentence for the lens, for a panel description or a CSV header line. */
export function lensText(lens: UsageLens): string {
  const chip = lens.hideAdmin ? 'admin screens hidden' : 'admin screens shown';
  return `Last ${lens.window} days · ${SURFACE_LABELS[lens.surface]} · ${PRESET_LABELS[presetOf(lens)]} · ${chip}`;
}

// ------------------------------------------------------------------------------------------------ small readers

const num = (value: unknown, fallback = 0): number => (typeof value === 'number' && Number.isFinite(value) ? value : fallback);
const numOrNull = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);
const str = (value: unknown, fallback = ''): string => (typeof value === 'string' ? value : fallback);
const strOrNull = (value: unknown): string | null => (typeof value === 'string' && value !== '' ? value : null);
/** A time as the service sends it: epoch milliseconds (a finite number) or an ISO string; null for anything else. */
const timeOrNull = (value: unknown): string | number | null => (typeof value === 'number' && Number.isFinite(value) ? value : strOrNull(value));
const list = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);
const record = (value: unknown): Record<string, unknown> => (value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {});
const numbers = (value: unknown): number[] => list(value).map((item) => num(item));
const strings = (value: unknown): string[] => list(value).filter((item): item is string => typeof item === 'string' && item !== '');

/** A `{ name, count }` list (entry screens, previous screens), largest first. */
export interface NamedCount { name: string; count: number }
function namedCounts(value: unknown, nameKeys: readonly string[] = ['name', 'screen'], countKeys: readonly string[] = ['count', 'events', 'sessions']): NamedCount[] {
  const rows: NamedCount[] = [];
  for (const item of list(value)) {
    const row = record(item);
    const name = nameKeys.map((key) => strOrNull(row[key])).find((found) => found !== null) ?? null;
    if (name === null) continue;
    const count = countKeys.map((key) => numOrNull(row[key])).find((found) => found !== null) ?? 0;
    rows.push({ name, count });
  }
  return rows.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

// ------------------------------------------------------------------------------------------------ features (ranked screens and actions)

export type FeatureKind = 'screen' | 'action';
export const KIND_LABELS: Record<FeatureKind, { one: string; many: string }> = { screen: { one: 'Screen', many: 'Screens' }, action: { one: 'Action', many: 'Actions' } };

/** The kind the service answers (`screen` or `screen_view`; `action` or `feature`), read as the page's two kinds. */
export function featureKind(value: unknown): FeatureKind | null {
  if (value === 'screen' || value === 'screen_view') return 'screen';
  if (value === 'action' || value === 'feature') return 'action';
  return null;
}

export interface FeatureRow {
  name: string;
  kind: FeatureKind;
  /** The kind as the service spelled it: what the detail route is asked with (`/features/:kind/:name`). */
  kindRaw: string;
  events: number;
  users: number;
  sessions: number;
  engagementMs: number;
  /** One value per day of the window, oldest first; a day without data is 0. */
  sparkline: number[];
}

/** GET /v1/admin/usage/features: `{ items: [{ name, kind, events, users, sessions, engagement_ms, sparkline }] }`. */
export function normalizeFeatures(wire: unknown): FeatureRow[] {
  const source = record(wire);
  const rows: FeatureRow[] = [];
  for (const item of list(source.items ?? source.features)) {
    const row = record(item);
    const name = strOrNull(row.name);
    const kind = featureKind(row.kind);
    if (name === null || kind === null) continue;
    rows.push({
      name,
      kind,
      kindRaw: str(row.kind, kind),
      events: num(row.events),
      users: num(row.users),
      sessions: num(row.sessions),
      engagementMs: num(row.engagement_ms ?? row.engagementMs),
      sparkline: numbers(row.sparkline ?? row.spark),
    });
  }
  return rows;
}

export type FeatureSortKey = 'name' | 'events' | 'users' | 'sessions' | 'engagementMs';
export interface FeatureSort { key: FeatureSortKey; direction: 'asc' | 'desc' }
export const DEFAULT_FEATURE_SORT: FeatureSort = { key: 'events', direction: 'desc' };

/**
 * The ranked list of one kind: the rows of that kind, those matching the search (case-insensitive, anywhere in the name),
 * sorted by the key (numbers descending first, the name ascending first; ties by name). Rank is the row's place in this list.
 */
export function rankFeatures(rows: readonly FeatureRow[], kind: FeatureKind, search = '', sort: FeatureSort = DEFAULT_FEATURE_SORT): FeatureRow[] {
  const needle = search.trim().toLowerCase();
  const picked = rows.filter((row) => row.kind === kind && (!needle || row.name.toLowerCase().includes(needle)));
  const sign = sort.direction === 'asc' ? 1 : -1;
  return picked.sort((a, b) => {
    if (sort.key === 'name') return a.name.localeCompare(b.name, 'en-US', { sensitivity: 'base' }) * sign;
    const diff = (a[sort.key] - b[sort.key]) * sign;
    return diff !== 0 ? diff : a.name.localeCompare(b.name, 'en-US', { sensitivity: 'base' });
  });
}

/** A header click: the same key flips the direction; another key starts numbers descending and the name ascending. */
export function toggleFeatureSort(sort: FeatureSort, key: FeatureSortKey): FeatureSort {
  if (sort.key === key) return { key, direction: sort.direction === 'asc' ? 'desc' : 'asc' };
  return { key, direction: key === 'name' ? 'asc' : 'desc' };
}

// ------------------------------------------------------------------------------------------------ the catalog ("unused")

export interface UsageCatalog {
  screens: string[];
  actions: string[];
  /** The names `hide_admin=1` leaves out: exact names, and a prefix spelled `admin:*`. */
  hidden: string[];
}

/** True for a name the hidden list covers: an exact name, or a `<prefix>*` entry it starts with. */
export function isHiddenName(name: string, hidden: readonly string[]): boolean {
  return hidden.some((entry) => (entry.endsWith('*') ? name.startsWith(entry.slice(0, -1)) : entry === name));
}

/**
 * GET /v1/admin/usage/catalog: every known screen and action name, as `{ screens: [...], actions: [...], hidden: [...] }` or as
 * `{ items: [{ name, kind }] }`; both are read. Names are deduplicated and sorted.
 */
export function normalizeCatalog(wire: unknown): UsageCatalog {
  const source = record(wire);
  const screens = new Set(strings(source.screens));
  const actions = new Set(strings(source.actions));
  for (const item of list(source.items ?? source.names)) {
    const row = record(item);
    const name = strOrNull(row.name);
    const kind = featureKind(row.kind);
    if (name === null || kind === null) continue;
    (kind === 'screen' ? screens : actions).add(name);
  }
  return { screens: [...screens].sort(), actions: [...actions].sort(), hidden: strings(source.hidden) };
}

/**
 * The catalog names of a kind that the ranked list does not hold: known to the app, never seen in this window and lens.
 * With the admin screens hidden, the hidden names are not "unused" either: the lens left them out of the list.
 */
export function unusedNames(catalog: UsageCatalog, rows: readonly FeatureRow[], kind: FeatureKind, hideAdmin = false): string[] {
  const seen = new Set(rows.filter((row) => row.kind === kind).map((row) => row.name));
  return (kind === 'screen' ? catalog.screens : catalog.actions).filter((name) => !seen.has(name) && !(hideAdmin && isHiddenName(name, catalog.hidden)));
}

// ------------------------------------------------------------------------------------------------ a feature's detail

export interface DayPoint { day: string; events: number; users: number; engagementMs: number }
export interface ParamCount { key: string; value: string; count: number }

export interface FeatureDetail {
  name: string;
  kind: FeatureKind;
  /** The window's totals, as the service sums them. */
  totals: { events: number; users: number; sessions: number; engagementMs: number };
  days: DayPoint[];
  params: ParamCount[];
  previous: NamedCount[];
  next: NamedCount[];
}

/**
 * GET /v1/admin/usage/features/:kind/:name: the window's `totals` (`{ events, users, sessions, engagement_ms }`), the daily
 * series (`days`, each `{ day, events, users, engagement_ms }`), the top params (`params`, each `{ key, value, count }`) and the
 * previous and next screens (`previous`, `next`, each `{ name, count }`).
 */
export function normalizeFeatureDetail(wire: unknown, fallback: { name: string; kind: FeatureKind }): FeatureDetail {
  const source = record(wire);
  const days: DayPoint[] = [];
  for (const item of list(source.days ?? source.series ?? source.daily)) {
    const row = record(item);
    const day = strOrNull(row.day ?? row.date);
    if (day === null) continue;
    days.push({ day, events: num(row.events), users: num(row.users), engagementMs: num(row.engagement_ms ?? row.engagementMs) });
  }
  days.sort((a, b) => a.day.localeCompare(b.day));
  const params: ParamCount[] = [];
  for (const item of list(source.params ?? source.top_params ?? source.topParams)) {
    const row = record(item);
    const key = strOrNull(row.key ?? row.param);
    if (key === null) continue;
    const value = row.value === undefined || row.value === null ? '' : String(row.value);
    params.push({ key, value, count: num(row.count ?? row.events) });
  }
  params.sort((a, b) => b.count - a.count || a.key.localeCompare(b.key) || a.value.localeCompare(b.value));
  const totals = record(source.totals);
  return {
    name: str(source.name, fallback.name),
    kind: featureKind(source.kind) ?? fallback.kind,
    totals: { events: num(totals.events), users: num(totals.users), sessions: num(totals.sessions), engagementMs: num(totals.engagement_ms ?? totals.engagementMs) },
    days,
    params,
    previous: namedCounts(source.previous ?? source.previous_screens ?? source.previousScreens),
    next: namedCounts(source.next ?? source.next_screens ?? source.nextScreens),
  };
}

// ------------------------------------------------------------------------------------------------ realtime

export interface RealtimeView {
  /** Active users in the last 5 and the last 30 minutes. */
  activeUsers: { m5: number; m30: number };
  /** Events per minute over the last 30 minutes, oldest first. */
  perMinute: number[];
  /** Top screens in the last 5 minutes. */
  topScreens: NamedCount[];
  /** Batches the service could not write (contract section 2: a failed write answers 503 and counts here). */
  writeFailures: number;
}

/**
 * GET /v1/admin/usage/realtime: `{ active_users: { m5, m30 }, events_per_minute: [...], top_screens: [{ name, count }], write_failures }`.
 * `events_per_minute` may be numbers or `{ minute, events }` points; `active_users` may also come as two flat fields.
 */
export function normalizeRealtime(wire: unknown): RealtimeView {
  const source = record(wire);
  const active = record(source.active_users ?? source.activeUsers);
  const perMinute = list(source.events_per_minute ?? source.eventsPerMinute ?? source.per_minute).map((item) => (typeof item === 'number' ? num(item) : num(record(item).events ?? record(item).count)));
  return {
    activeUsers: {
      m5: num(active.m5 ?? active['5m'] ?? active.last_5m ?? source.active_users_5m ?? source.activeUsers5m),
      m30: num(active.m30 ?? active['30m'] ?? active.last_30m ?? source.active_users_30m ?? source.activeUsers30m),
    },
    perMinute,
    topScreens: namedCounts(source.top_screens ?? source.topScreens, ['name', 'screen'], ['count', 'events']),
    writeFailures: num(source.write_failures ?? source.writeFailures),
  };
}

// ------------------------------------------------------------------------------------------------ sessions and the overview

export interface SessionsView {
  count: number;
  engaged: number;
  /** The median session length in milliseconds, or null with no sessions. */
  medianMs: number | null;
  buckets: Array<{ label: string; count: number }>;
  /** 24 counts, hour 0 to 23 in America/Los_Angeles. */
  byHour: number[];
  /** 7 counts, Monday first. */
  byWeekday: number[];
  /** Distinct people and events in the window (the Overview tiles). */
  users: number;
  events: number;
  /** The same figures for the window just before (the tiles' deltas); null when the service sends none. */
  previous: { count: number | null; engaged: number | null; users: number | null; events: number | null } | null;
  /** The per-day series for the trend, oldest first. */
  daily: Array<{ day: string; sessions: number; users: number; events: number }>;
}

const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/** A series keyed by index: 24 numbers, or `{ hour, count }` points, or an object keyed by the index. */
function indexedSeries(value: unknown, size: number, indexKeys: readonly string[]): number[] {
  const out = Array.from({ length: size }, () => 0);
  if (Array.isArray(value) && value.every((item) => typeof item === 'number') && value.length === size) return value.map((item) => num(item));
  if (Array.isArray(value)) {
    for (const item of value) {
      const row = record(item);
      const index = indexKeys.map((key) => numOrNull(row[key])).find((found) => found !== null);
      if (index !== undefined && index !== null && index >= 0 && index < size) out[index] = num(row.count ?? row.sessions ?? row.events);
    }
    return out;
  }
  for (const [key, item] of Object.entries(record(value))) {
    const index = WEEKDAY_NAMES.findIndex((name) => name.toLowerCase() === key.slice(0, 3).toLowerCase());
    const at = Number.isInteger(Number(key)) ? Number(key) : index;
    if (at >= 0 && at < size) out[at] = num(item);
  }
  return out;
}

/**
 * GET /v1/admin/usage/sessions: `{ count, users, events, engaged, median_ms, buckets: [{ label, count }], by_hour: [24],
 * by_weekday: [7], previous: { count, engaged, users, events }, daily: [{ day, sessions, users, events }] }` (the Overview reads
 * this route for its tiles and trend).
 */
export function normalizeSessions(wire: unknown): SessionsView {
  const source = record(wire);
  const previous = source.previous === undefined || source.previous === null ? null : record(source.previous);
  const buckets = list(source.buckets ?? source.length_buckets ?? source.lengthBuckets).map((item) => {
    const row = record(item);
    return { label: str(row.label ?? row.bucket, '—'), count: num(row.count ?? row.sessions) };
  });
  const daily: SessionsView['daily'] = [];
  for (const item of list(source.daily ?? source.days)) {
    const row = record(item);
    const day = strOrNull(row.day ?? row.date);
    if (day === null) continue;
    daily.push({ day, sessions: num(row.sessions ?? row.count), users: num(row.users), events: num(row.events) });
  }
  daily.sort((a, b) => a.day.localeCompare(b.day));
  return {
    count: num(source.count ?? source.sessions),
    engaged: num(source.engaged),
    medianMs: numOrNull(source.median_ms ?? source.medianMs ?? source.median_length_ms),
    buckets,
    byHour: indexedSeries(source.by_hour ?? source.byHour, 24, ['hour']),
    byWeekday: indexedSeries(source.by_weekday ?? source.byWeekday, 7, ['weekday', 'day']),
    users: num(source.users),
    events: num(source.events),
    previous: previous ? { count: numOrNull(previous.count ?? previous.sessions), engaged: numOrNull(previous.engaged), users: numOrNull(previous.users), events: numOrNull(previous.events) } : null,
    daily,
  };
}

/** `Mon` to `Sun`, Monday first (the contract's weeks start on Monday). */
export function weekdayName(index: number): string {
  return WEEKDAY_NAMES[((index % 7) + 7) % 7] ?? '—';
}

/** `10:00` for hour 10; `—` outside 0 to 23. */
export function hourLabel(hour: number): string {
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) return '—';
  return `${String(hour).padStart(2, '0')}:00`;
}

/** The index of the largest value, or null when every value is 0 (nothing to call the busiest). */
export function peakIndex(values: readonly number[]): number | null {
  let best: number | null = null;
  values.forEach((value, index) => {
    if (value > 0 && (best === null || value > (values[best] ?? 0))) best = index;
  });
  return best;
}

/** The change from the previous window: `{ pct }` (a ratio, 0.12 for +12 %), or `pct: null` with nothing to compare. */
export interface Delta { current: number | null; previous: number | null; pct: number | null }

export function delta(current: number | null | undefined, previous: number | null | undefined): Delta {
  const now = numOrNull(current);
  const before = numOrNull(previous);
  if (now === null || before === null || before <= 0) return { current: now, previous: before, pct: null };
  return { current: now, previous: before, pct: (now - before) / before };
}

/** `+12 % on the previous 30 days`; `nothing in the previous 30 days to compare` when there is no earlier figure. */
export function deltaText(change: Delta, windowDays: number): string {
  const previous = `the previous ${windowDays} day${windowDays === 1 ? '' : 's'}`;
  if (change.pct === null) return `nothing in ${previous} to compare`;
  const rounded = Math.round(change.pct * 100);
  return `${rounded > 0 ? '+' : ''}${rounded} % on ${previous}`;
}

/** The engaged share of sessions, 0 to 1 (0 with no sessions). */
export function engagedShare(view: Pick<SessionsView, 'count' | 'engaged'>): number {
  return view.count > 0 ? view.engaged / view.count : 0;
}

// ------------------------------------------------------------------------------------------------ journeys

export interface Transition { from: string; to: string; count: number }
export interface JourneyPath { steps: string[]; count: number }
export interface JourneysView {
  entries: NamedCount[];
  exits: NamedCount[];
  transitions: Transition[];
  /** The focus screen's neighbours, when a focus was asked for. */
  focus: { name: string; previous: NamedCount[]; next: NamedCount[] } | null;
  paths: JourneyPath[];
}

/**
 * GET /v1/admin/usage/journeys?focus=: `{ entries: [{ name, count }], exits: [...], transitions: [{ from, to, count }],
 * focus: { name, previous: [...], next: [...] } | null, paths: [{ steps: [a, b, c], count }] }`.
 */
export function normalizeJourneys(wire: unknown, focusAsked: string | null): JourneysView {
  const source = record(wire);
  const transitions: Transition[] = [];
  for (const item of list(source.transitions)) {
    const row = record(item);
    const from = strOrNull(row.from);
    const to = strOrNull(row.to);
    if (from === null || to === null) continue;
    transitions.push({ from, to, count: num(row.count) });
  }
  transitions.sort((a, b) => b.count - a.count || a.from.localeCompare(b.from) || a.to.localeCompare(b.to));
  const paths: JourneyPath[] = [];
  for (const item of list(source.paths ?? source.top_paths ?? source.topPaths)) {
    const row = record(item);
    const steps = strings(row.steps ?? row.path);
    if (!steps.length) continue;
    paths.push({ steps, count: num(row.count) });
  }
  paths.sort((a, b) => b.count - a.count || a.steps.join(' ').localeCompare(b.steps.join(' ')));
  const focusRecord = source.focus === undefined || source.focus === null ? null : record(source.focus);
  const focusName = focusRecord ? strOrNull(focusRecord.name ?? focusRecord.screen) ?? focusAsked : focusAsked;
  const focus = focusName && (focusRecord || focusAsked)
    ? { name: focusName, previous: namedCounts(focusRecord?.previous), next: namedCounts(focusRecord?.next) }
    : null;
  return {
    entries: namedCounts(source.entries ?? source.entry_screens ?? source.entryScreens),
    exits: namedCounts(source.exits ?? source.exit_screens ?? source.exitScreens),
    transitions,
    focus,
    paths,
  };
}

/** Every screen a journeys answer names, sorted: the choices for the focus select. */
export function journeyScreens(view: JourneysView, catalog: UsageCatalog | null = null): string[] {
  const names = new Set<string>(catalog?.screens ?? []);
  for (const row of [...view.entries, ...view.exits]) names.add(row.name);
  for (const row of view.transitions) { names.add(row.from); names.add(row.to); }
  for (const path of view.paths) for (const step of path.steps) names.add(step);
  if (view.focus) {
    names.add(view.focus.name);
    for (const row of [...view.focus.previous, ...view.focus.next]) names.add(row.name);
  }
  return [...names].sort();
}

// ------------------------------------------------------------------------------------------------ retention

export type RetentionWeeks = 4 | 8 | 12 | 26;
export const RETENTION_WEEKS: readonly RetentionWeeks[] = [4, 8, 12, 26];
export const DEFAULT_RETENTION_WEEKS: RetentionWeeks = 8;

export interface Cohort {
  /** The Monday the cohort's week starts on (UTC), `yyyy-mm-dd`. */
  week: string;
  users: number;
  /** The share of the cohort seen in each week from its own, 0 to 1: index 0 is the cohort week itself (W0, always 1 when anyone is in it), index 1 the week after (W1). A week not yet reached is null. */
  rates: Array<number | null>;
  /** The first cohort: it includes everyone who was already using Gyro when collection began. */
  includesExisting: boolean;
}

export interface RetentionView { weeks: number; cohorts: Cohort[] }

/**
 * GET /v1/admin/usage/retention?weeks=: `{ weeks, cohorts: [{ week, users, rates: [...], includes_existing }] }`. A rate over 1
 * is read as a percentage (62 → 0.62); the oldest cohort is the one flagged when the service flags none.
 */
export function normalizeRetention(wire: unknown, weeksAsked: number): RetentionView {
  const source = record(wire);
  const cohorts: Cohort[] = [];
  for (const item of list(source.cohorts)) {
    const row = record(item);
    const week = strOrNull(row.week ?? row.cohort ?? row.start);
    if (week === null) continue;
    const rates = list(row.rates ?? row.returned ?? row.weeks).map((value) => {
      const rate = numOrNull(value);
      if (rate === null) return null;
      return Math.max(0, Math.min(1, rate > 1 ? rate / 100 : rate));
    });
    cohorts.push({ week, users: num(row.users ?? row.size), rates, includesExisting: row.includes_existing === true || row.includesExisting === true });
  }
  cohorts.sort((a, b) => a.week.localeCompare(b.week));
  if (cohorts.length && !cohorts.some((cohort) => cohort.includesExisting)) cohorts[0]!.includesExisting = true;
  return { weeks: num(source.weeks, weeksAsked), cohorts };
}

/** The heat step for a rate: 0 (none) to 4 (most), for the cohort cells' tint. */
export function heatStep(rate: number | null): 0 | 1 | 2 | 3 | 4 {
  if (rate === null || rate <= 0) return 0;
  if (rate < 0.15) return 1;
  if (rate < 0.35) return 2;
  if (rate < 0.6) return 3;
  return 4;
}

// ------------------------------------------------------------------------------------------------ people

export interface PersonRow {
  upn: string;
  oid: string | null;
  sessions: number;
  events: number;
  /** Epoch milliseconds (as the service sends it) or an ISO time; null never seen. */
  lastSeen: string | number | null;
  topScreen: string | null;
  surfaces: string[];
}

/** GET /v1/admin/usage/people: `{ people: [{ upn, oid, sessions, events, last_seen, top_screen, surfaces }] }`, up to 200. */
export function normalizePeople(wire: unknown): PersonRow[] {
  const source = record(wire);
  const rows: PersonRow[] = [];
  for (const item of list(source.people ?? source.items)) {
    const row = record(item);
    const upn = strOrNull(row.upn ?? row.email);
    if (upn === null) continue;
    rows.push({
      upn: upn.toLowerCase(),
      oid: strOrNull(row.oid),
      sessions: num(row.sessions),
      events: num(row.events),
      lastSeen: timeOrNull(row.last_seen ?? row.lastSeen),
      topScreen: strOrNull(row.top_screen ?? row.topScreen),
      surfaces: strings(row.surfaces),
    });
  }
  return rows.sort((a, b) => b.sessions - a.sessions || b.events - a.events || a.upn.localeCompare(b.upn));
}

/** A directory name by lower-cased account, from GET /v1/admin/people (a `displayName` or `name` beside each `email`, when the service has one). */
export function directoryNames(wire: unknown): Map<string, string> {
  const names = new Map<string, string>();
  for (const item of list(record(wire).people)) {
    const row = record(item);
    const email = strOrNull(row.email ?? row.upn);
    const name = strOrNull(row.displayName ?? row.name);
    if (email !== null && name !== null) names.set(email.toLowerCase(), name);
  }
  return names;
}

/** The name shown for an account: the directory name when known, else the account itself. */
export function personLabel(upn: string, names: ReadonlyMap<string, string>): string {
  return names.get(upn.toLowerCase()) ?? upn;
}

// ------------------------------------------------------------------------------------------------ tech

export interface SplitRow { key: string; users: number; sessions: number }
export interface TechView { surfaces: SplitRow[]; appVersions: SplitRow[]; osVersions: SplitRow[]; deviceClasses: SplitRow[] }

function splitRows(value: unknown, keyNames: readonly string[]): SplitRow[] {
  const rows: SplitRow[] = [];
  for (const item of list(value)) {
    const row = record(item);
    const key = [...keyNames, 'key', 'name', 'value'].map((name) => strOrNull(row[name])).find((found) => found !== null);
    if (key === undefined || key === null) continue;
    rows.push({ key, users: num(row.users), sessions: num(row.sessions) });
  }
  return rows.sort((a, b) => b.users - a.users || b.sessions - a.sessions || a.key.localeCompare(b.key));
}

/** GET /v1/admin/usage/tech: `{ surfaces: [{ key, users, sessions }], app_versions: [...], os_versions: [...], device_classes: [...] }`. */
export function normalizeTech(wire: unknown): TechView {
  const source = record(wire);
  return {
    surfaces: splitRows(source.surfaces ?? source.surface, ['surface']),
    appVersions: splitRows(source.app_versions ?? source.appVersions ?? source.app_version, ['app_version', 'version']),
    osVersions: splitRows(source.os_versions ?? source.osVersions ?? source.os_version, ['os_version', 'os']),
    deviceClasses: splitRows(source.device_classes ?? source.deviceClasses ?? source.device_class, ['device_class', 'device']),
  };
}

/** The users of every row together: the denominator of a split's shares (a person on two versions counts in both). */
export function splitTotal(rows: readonly SplitRow[], of: 'users' | 'sessions' = 'users'): number {
  return rows.reduce((sum, row) => sum + row[of], 0);
}

// ------------------------------------------------------------------------------------------------ the audience (contract section 2, admin only)

export interface AudiencePerson {
  upn: string;
  oid: string | null;
  /** `admin`: marked by an IT admin; `group:<id>`: imported from that directory group. */
  source: string;
  setBy: string | null;
  /** Epoch milliseconds (as the service sends it) or an ISO time. */
  setAt: string | number | null;
}
export interface AudienceGroup { id: string; label: string }
export interface AudienceView { people: AudiencePerson[]; hidden: string[]; groups: AudienceGroup[] }

/** GET /v1/admin/usage/audience: `{ people: [{ upn, oid?, audience, source, set_by, set_at }], hidden: [names], groups: [{ id, label }] }`. */
export function normalizeAudience(wire: unknown): AudienceView {
  const source = record(wire);
  const people: AudiencePerson[] = [];
  for (const item of list(source.people)) {
    const row = record(item);
    const upn = strOrNull(row.upn ?? row.email);
    if (upn === null) continue;
    people.push({ upn: upn.toLowerCase(), oid: strOrNull(row.oid), source: str(row.source, 'admin'), setBy: strOrNull(row.set_by ?? row.setBy), setAt: timeOrNull(row.set_at ?? row.setAt) });
  }
  people.sort((a, b) => a.upn.localeCompare(b.upn));
  const groups: AudienceGroup[] = [];
  for (const item of list(source.groups)) {
    const row = record(item);
    const id = strOrNull(row.id ?? row.group_id ?? row.groupId);
    if (id === null) continue;
    groups.push({ id, label: str(row.label ?? row.name, id) });
  }
  return { people, hidden: strings(source.hidden ?? source.hidden_names).sort(), groups };
}

/** POST /v1/admin/usage/audience/import answers `{ group_id, members, marked, unaddressed, ...the audience view }`. */
export interface ImportOutcome { members: number; marked: number; unaddressed: number }

export function normalizeImportOutcome(wire: unknown): ImportOutcome {
  const source = record(wire);
  return { members: num(source.members), marked: num(source.marked), unaddressed: num(source.unaddressed) };
}

/** "Every member of USER-QA is marked internal: 7 marked, 1 without an account." */
export function importOutcomeText(label: string, outcome: ImportOutcome): string {
  const parts = [`${outcome.marked.toLocaleString('en-US')} marked`];
  if (outcome.unaddressed > 0) parts.push(`${outcome.unaddressed.toLocaleString('en-US')} without an account`);
  return `Every member of ${label} is marked internal: ${parts.join(', ')}.`;
}

/** The group id of a `group:<id>` source, or null for an admin's mark. */
export function sourceGroupId(source: string): string | null {
  return source.startsWith('group:') && source.length > 'group:'.length ? source.slice('group:'.length) : null;
}

/** "Marked by IT" for an admin's mark; "From group QA" for an import (the group's label, or its id when the read names no label). */
export function sourceLabel(source: string, groups: readonly AudienceGroup[]): string {
  const id = sourceGroupId(source);
  if (id === null) return 'Marked by IT';
  const group = groups.find((candidate) => candidate.id === id);
  return `From group ${group?.label ?? id}`;
}

/** The marked accounts, lower-cased, for the eye icon on a people row. */
export function internalSet(view: AudienceView | null | undefined): Set<string> {
  return new Set((view?.people ?? []).map((person) => person.upn));
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The accounts typed into "Add by account": split on commas, semicolons, spaces and lines, lower-cased, one each; what is not an address is reported. */
export function typedAccounts(text: string): { upns: string[]; rejected: string[] } {
  const upns = new Set<string>();
  const rejected = new Set<string>();
  for (const word of text.split(/[\s,;]+/)) {
    const clean = word.trim().replace(/^<|>$/g, '').replace(/^mailto:/i, '').toLowerCase();
    if (!clean) continue;
    if (EMAIL.test(clean) && clean.length <= 320) upns.add(clean);
    else rejected.add(word.trim());
  }
  return { upns: [...upns], rejected: [...rejected] };
}

// ------------------------------------------------------------------------------------------------ labels and durations

/** `42 s`, `3 min`, `1 h 5 min` from milliseconds; an em dash for nothing. Rounded to whole minutes first, so never `60 min`. */
export function durationText(ms: number | null | undefined): string {
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms < 0) return '—';
  const seconds = ms / 1000;
  if (seconds < 59.5) return `${Math.round(seconds)} s`;
  const totalMinutes = Math.round(seconds / 60);
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
}

/** The average engagement per session (or per user when there are no sessions), as a duration; an em dash with neither. */
export function engagementPer(row: Pick<FeatureRow, 'engagementMs' | 'sessions' | 'users'>): string {
  const per = row.sessions > 0 ? row.sessions : row.users;
  return per > 0 ? durationText(row.engagementMs / per) : '—';
}

/** A screen or action name as people read it: `sidepanel:diagnostics` → `sidepanel › diagnostics`, `gyro_ask` → `gyro ask`. */
export function nameLabel(name: string): string {
  return name.split(':').join(' › ').replace(/_/g, ' ');
}

/** The surface the contract names, as people read it; an unknown value as it came. */
export function surfaceLabel(surface: string): string {
  return isSurface(surface) ? SURFACE_LABELS[surface] : surface;
}

// ------------------------------------------------------------------------------------------------ dates (Monday weeks, UTC)

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** `2026-10-24` plus `n` days, in UTC; the input back when it is not a date. */
export function addDays(day: string, n: number): string {
  if (!DATE.test(day)) return day;
  const at = Date.parse(`${day}T00:00:00Z`);
  if (!Number.isFinite(at)) return day;
  return new Date(at + n * 86_400_000).toISOString().slice(0, 10);
}

/** The Monday of the UTC week a day is in (a Monday is its own). */
export function mondayOf(day: string): string {
  if (!DATE.test(day)) return day;
  const at = Date.parse(`${day}T00:00:00Z`);
  if (!Number.isFinite(at)) return day;
  const weekday = new Date(at).getUTCDay(); // 0 Sunday … 6 Saturday
  return addDays(day, -((weekday + 6) % 7));
}

/** `Oct 26` for a day; `Oct 26, 2026` with the year; an em dash for anything that is not a date. */
export function dayLabel(day: string, withYear = false): string {
  if (!DATE.test(day)) return '—';
  const at = Date.parse(`${day}T00:00:00Z`);
  if (!Number.isFinite(at)) return '—';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC' }).format(new Date(at));
}

/** `Week of Oct 26`: a cohort's row label. */
export function weekLabel(monday: string): string {
  return `Week of ${dayLabel(mondayOf(monday))}`;
}

// ------------------------------------------------------------------------------------------------ CSV (the export, with the formula-injection guard)

/**
 * One CSV cell. A cell that a spreadsheet would read as a formula (starting with `=`, `+`, `-`, `@`, a tab or a carriage
 * return) is prefixed with an apostrophe, so a screen named `=HYPERLINK(...)` opens as text; then the cell is quoted when it
 * holds a comma, a quote or a line break, with quotes doubled.
 */
export function csvCell(value: string | number | null | undefined): string {
  let text = value === null || value === undefined ? '' : String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** A CSV document: a header row then the rows, CRLF line endings, every cell guarded. */
export function csvOf(header: readonly string[], rows: ReadonlyArray<ReadonlyArray<string | number | null | undefined>>): string {
  return [header, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n') + '\r\n';
}

/** The ranked list as CSV: rank, name, kind, events, users, sessions, engagement in seconds and per session. */
export function featuresCsv(rows: readonly FeatureRow[]): string {
  return csvOf(
    ['rank', 'name', 'kind', 'events', 'users', 'sessions', 'engagement_s', 'engagement_per_session_s'],
    rows.map((row, index) => [index + 1, row.name, row.kind, row.events, row.users, row.sessions, Math.round(row.engagementMs / 1000), row.sessions > 0 ? Math.round(row.engagementMs / row.sessions / 1000) : 0]),
  );
}

/** `gyro-usage-screens-30d-general-2026-10-29.csv`: the export's file name. */
export function csvFileName(kind: FeatureKind, lens: UsageLens, today: Date = new Date()): string {
  return `gyro-usage-${kind}s-${lens.window}d-${lens.audience}-${today.toISOString().slice(0, 10)}.csv`;
}
